import type {
  GetContractsResponse,
  GetContractDetailResponse,
  GetDashboardInfoResponse,
  GetDocumentsResponse,
  AccountManager,
  ContractOverview,
  InsuranceCasesOverview,
  MessagesOverview,
  Notice,
  GetClientsForCustomerResponse,
  GetClientInformationResponse,
  Client,
  ClientInformation,
  Address,
  Message,
  Contract,
  ContractAttribute,
  ContractDetail,
  InsuranceCase,
  Risk,
  Document,
  Payment,
  GetSubcontractsResponse,
  GetInsuranceCasesResponse,
  GetInsuranceCaseDetailResponse,
  GetPaymentsResponse,
  GetSearchResponse,
  GetMessagesResponse,
  InsuranceCaseDetail,
  Participant,
  Settlement,
  InsuranceCaseComs,
} from '@/types/customer'
import type { PostMessageResponse, SetMessageStatusResponse } from '@/types/message'
import type { CustomerDataService } from '@/types/customer-data-service'
import {
  ContractValidity,
  ContractType,
  ContractStatus,
  InsuranceCaseStatus,
  ClassificationType,
  MessageType,
  MessageStatus,
  MessageBindingType,
} from '@/types/customer'
import { SearchTarget } from '@/types/search'
import { toEnumValue } from '@/utils/enum'
import type {
  DashboardResponse,
  Klient,
  KlientDetailDto,
  Adresa,
  SmlouvaDetailResponse,
  SmlouvaList,
  ValueParItem,
  Spravce,
  SmlouvaRizikoList,
  Priloha,
  PrilohaPu,
  DownloadPrilohaData,
  PodsmlouvaList,
  PodsmlouvaDetail,
  PojistnaUdalostDetail,
  PojistnaUdalostDetailData,
  Platba,
  PojistnaUdalostUcastnik,
  PojistnaUdalostKomunikace,
  PojistnaUdalostFinance,
  PojistnaUdalostFinanceZaloha,
  Poznamka,
  FulltextResult,
  SmlouvaFulltext,
  UkolInsert,
  UkolStatusArg,
  SouborInfo,
  Ukol,
} from '@/api-client/types.gen'
import {
  getApiDashboardByKlientId,
  getApiKlientGetklientlist,
  klientDetail,
  smlouvyList,
  smlouvaDetail,
  prilohySmlouvaDetail,
  prilohyPodsmlouvaDetail,
  prilohyPojistnaUdalost,
  prilohyObecne,
  prilohyKlientDetail,
  prilohyUkolDetail,
  prilohaKey,
  podsmlouvyList,
  podsmlouvaDetail,
  platbyList,
  pojistneUdalosti,
  pojistneUdalostiVse,
  pojistnaUdalost,
  vyhledat,
  zpravaInsert,
  zpravaStatus,
  zpravyList,
  zpravySmlouvaList,
  zpravyPodsmlouvaList,
  zpravyPojistnaUdalostList,
  postApiUploadDeleteByUid,
  postApiAccountChangepassword,
} from '@/api-client/sdk.gen'
import { client as _ApiClient } from '@/api-client/client.gen'
import { classifyApiError } from '@/utils/errorClassifier'
import { AxiosError } from 'axios'
import type { AxiosResponse } from 'axios'

export class CustomerDataServiceImpl implements CustomerDataService {
  private throwIfError<T>(
    response: AxiosResponse<T> | AxiosError | (AxiosError & { error: unknown }),
    defaultMessage: string,
  ): asserts response is AxiosResponse<T> {
    if (response instanceof AxiosError || 'error' in response) {
      throw classifyApiError(response, defaultMessage)
    }
  }
  async getClientsForCustomer(): Promise<GetClientsForCustomerResponse> {
    console.log('getClientsForCustomer API call')
    const response = await getApiKlientGetklientlist()
    this.throwIfError(response, 'Error fetching clients for customer.')
    const data = response.data
    return { success: true, clients: this.mapClients(data || []) }
  }

  async getClientInformation(clientId: number): Promise<GetClientInformationResponse> {
    console.log('getClientInformation API call', clientId)
    const response = await klientDetail({ path: { klientID: clientId } })
    this.throwIfError(response, 'Error fetching client information.')
    const data = response.data
    return { success: true, clientInformation: this.mapClientInformation(data || {}) }
  }

  async getDashboardInfo(clientId: number): Promise<GetDashboardInfoResponse> {
    console.log('getDashboardInfo API call', clientId)
    const response = await getApiDashboardByKlientId({ path: { klientId: clientId } })
    this.throwIfError(response, 'Error fetching dashboard info.')
    const data = response.data
    return {
      success: true,
      dashboard: {
        accountManager: this.mapAccountManager(data!.spravce || []),
        contractsOverview: this.mapContractsOverview(data!),
        insurancesOverview: this.mapInsurancesOverview(data!),
        noticeBoard: this.mapNoticeBoard(data!),
        messagesOverview: this.mapMessagesOverview(data!),
      },
    }
  }

  private getInitials(name: string): string {
    if (!name || typeof name !== 'string') return ''

    return name
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0)
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
  }

  private mapClients(clients: Klient[]): Client[] {
    return (
      clients?.map((client) => ({
        id: client.klientID || 0,
        name: client.klientNazev || '',
      })) || []
    )
  }

  private mapClientInformation(client: KlientDetailDto): ClientInformation {
    const addresses = client.adresy || []
    return {
      name: client.klientNazev || '',
      type: client.klientTyp || '',
      ic: client.ic || '',
      birthDate: this.mapDate(client.datumNarozeni || ''),
      phones: this.extractPhones(addresses),
      emails: this.extractEmails(addresses),
      addresses: this.mapClientAddresses(addresses),
    }
  }

  private mapClientAddresses(addresses: Adresa[]): Address[] {
    return (
      addresses?.map((address) => ({
        type: address.adresaTyp || '',
        street: this.formatStreetLine(address.ulice || '', address.cp || '', address.co || ''),
        city: address.mesto || '',
        zipCode: address.psc || '',
      })) || []
    )
  }

  private formatStreetLine(
    street: string,
    buildingNumber: string,
    orientationNumber: string,
  ): string {
    const baseStreet = street.trim()
    const cp = buildingNumber.trim()
    const co = orientationNumber.trim()
    const numberPart = cp && co ? `${cp}/${co}` : cp || co
    return [baseStreet, numberPart].filter(Boolean).join(' ').trim()
  }

  private extractPhones(addresses: Adresa[]): string[] {
    return Array.from(
      new Set(addresses.map((address) => (address.telefon || '').trim()).filter(Boolean)),
    )
  }

  private extractEmails(addresses: Adresa[]): string[] {
    return Array.from(
      new Set(addresses.map((address) => (address.mail || '').trim()).filter(Boolean)),
    )
  }

  private mapContractsOverview(dashboard: DashboardResponse): ContractOverview[] {
    return (
      dashboard?.monitorSmlouvyList?.map((item) => ({
        segment: item.segmentNazev || '',
        count: item.pocet || 0,
      })) || []
    )
  }

  private mapAccountManager(managers: Spravce[]): AccountManager[] {
    return (
      managers.map((item) => ({
        name: item.spravceNazev || '',
        email: item.mail || '',
        phone: item.telefon || '',
        initials: this.getInitials(item.spravceNazev || ''),
        classification: item.zarazeni || '',
        classificationType: this.mapClassificationType(item.zarazeniTypID),
      })) || []
    )
  }

  private mapClassificationType(zarazeniTypID: number | undefined): ClassificationType | undefined {
    switch (zarazeniTypID) {
      case 1:
        return ClassificationType.contractManager
      case 2:
        return ClassificationType.insuranceCaseManager
      default:
        return undefined
    }
  }

  private mapNoticeBoard(dashboard: DashboardResponse): Notice[] {
    return (
      dashboard?.nastenkaList?.map((nastenka) => {
        const rtf = nastenka.nastenkaData || ''
        const description = rtf
        return {
          id: nastenka.nastenkaID || 0,
          severity: nastenka.nastenkaDulezitost || 0,
          description: description,
        }
      }) || []
    )
  }

  private mapMessagesOverview(dashboard: DashboardResponse): MessagesOverview[] {
    const ukoly = dashboard?.monitorUkoly
    if (!ukoly) return []

    return [
      { type: MessageType.sent, count: ukoly.odeslanePocet || 0 },
      { type: MessageType.received, count: ukoly.prijatePocet || 0 },
    ]
  }

  private mapInsurancesOverview(dashboard: DashboardResponse): InsuranceCasesOverview[] {
    const insurance = dashboard?.monitorPojistneUdalosti || []
    return (
      insurance.map((item) => ({
        segment: item.segmentNazev || '',
        count: item.udalostiPocet || 0,
      })) || []
    )
  }

  async getContracts(
    clientId: number,
    active: boolean = true,
    expired: boolean = false,
    canceled: boolean = false,
  ): Promise<GetContractsResponse> {
    console.log('getContracts API call', clientId)
    const response = await smlouvyList({
      path: { klientId: clientId },
      query: { isAktivni: active, isUkoncene: expired, isStornovane: canceled },
    })
    this.throwIfError(response, 'Error fetching contracts.')
    const data = response.data
    return { success: true, contracts: data?.map((smlouva) => this.mapContract(smlouva)) || [] }
  }

  private mapContract(smlouva: SmlouvaList | SmlouvaFulltext): Contract {
    const data = 'data' in smlouva ? smlouva.data : undefined
    return {
      id: smlouva.smlouvaID || 0,
      type: toEnumValue(ContractType, smlouva.smlouvaTyp, ContractType.single),
      contractNumber: smlouva.smlouvaCislo || '',
      startDate: this.mapDate(smlouva.smlouvaPocatek || ''),
      endDate: this.mapDate(smlouva.smlouvaKonec || ''),
      partner: smlouva.partnerNazev || '',
      segment: smlouva.segmentNazev || '',
      subsegment: smlouva.podsegmentNazev || '',
      product: smlouva.produktPopis || '',
      attributes: this.mapContractAttributes(data || []),
      validity: toEnumValue(ContractValidity, smlouva.smlouvaPlatnostKod, ContractValidity.term),
      status: this.mapContractStatus(smlouva.smlouvaStatus),
      cancellationReason: null, // SmlouvaList and SmlouvaFulltext don't have stornoPopis
    }
  }
  private mapDate(date: string): Date | null {
    return date ? new Date(date) : null
  }

  private mapContractAttributes(data: ValueParItem[]): ContractAttribute[] {
    return (
      data?.map((item) => ({
        name: item.name || '',
        value: item.value || '',
        toolTip: item.toolTip || '',
      })) || []
    )
  }

  async getContractDetail(
    clientId: number,
    contractId: number,
    type: ContractType,
  ): Promise<GetContractDetailResponse> {
    console.log('getContractDetail API call', contractId)
    const response = await smlouvaDetail({
      path: { klientId: clientId, smlouvaId: contractId, typ: type },
    })
    this.throwIfError(response, 'Error fetching contract detail.')
    const data = response.data
    return { success: true, contract: this.mapContractDetail(data!) }
  }
  private mapContractDetail(smlouva: SmlouvaDetailResponse): ContractDetail {
    return {
      id: smlouva.smlouvaID || 0,
      contractNumber: smlouva.smlouvaCislo || '',
      startDate: this.mapDate(smlouva.smlouvaPocatek || ''),
      endDate: this.mapDate(smlouva.smlouvaKonec || ''),
      validity: toEnumValue(ContractValidity, smlouva.smlouvaPlatnostKod, ContractValidity.term),
      partner: smlouva.partnerNazev || '',
      premium: smlouva.lhutniPojistne || 0,
      renewalDate: this.mapDate(smlouva.obnovaSmlouvy || ''),
      segment: smlouva.segmentNazev || '',
      subsegment: smlouva.podsegmentNazev || '',
      policyholder: smlouva.pojistnikNazev || '',
      risks: this.mapRisks(smlouva.rizikaList || []),
      product: smlouva.produktPopis || '',
      attributes: this.mapContractAttributes(smlouva.atributyList || []),
      type: toEnumValue(ContractType, smlouva.smlouvaTyp, ContractType.single),
      annualPremium: smlouva.rocniPojistne || 0,
      paymentFrequency: smlouva.smlouvaSplatnostPopis || '',
      status: this.mapContractStatus(smlouva.smlouvaStatus),
      cancellationReason: smlouva.stornoPopis ?? null,
      accountManager: this.mapAccountManager([
        {
          spravceNazev: smlouva.spravceSmlouvy || '',
          mail: smlouva.spravceMail || '',
          telefon: smlouva.spravceTelefon || '',
        },
      ]),
    }
  }
  private mapRisks(risks: SmlouvaRizikoList[]): Risk[] {
    return (
      risks.map((risk) => ({
        description: risk.rizikoPopis || '',
        limit: risk.rizikoLimit || '',
        amount: risk.rizikoPojistnaCastka || 0,
        share: risk.rizikoSpoluucast || '',
        scope: risk.rizikoRozsah || '',
      })) || []
    )
  }
  private mapContractStatus(smlouvaStatus: number | undefined): ContractStatus {
    switch (smlouvaStatus) {
      case 30:
        return ContractStatus.active
      case 40:
        return ContractStatus.inactive
      case 50:
        return ContractStatus.cancelled
      default:
        return ContractStatus.active
    }
  }

  async getContractDocuments(clientId: number, contractId: number): Promise<GetDocumentsResponse> {
    console.log('getContractDocuments API call', contractId)
    const response = await prilohySmlouvaDetail({
      path: { klientId: clientId, smlouvaId: contractId },
    })
    this.throwIfError(response, 'Error fetching contract documents.')
    const data = response.data
    return { success: true, documents: this.mapDocuments(data || []) }
  }

  async getSubcontractDocuments(
    clientId: number,
    subcontractId: number,
  ): Promise<GetDocumentsResponse> {
    console.log('getSubcontractDocuments API call', subcontractId)
    const response = await prilohyPodsmlouvaDetail({
      path: { klientId: clientId, atributID: subcontractId },
    })
    this.throwIfError(response, 'Error fetching subcontract documents.')
    const data = response.data
    return { success: true, documents: this.mapDocuments(data || []) }
  }

  async getInsuranceCaseDocuments(clientId: number, caseId: number): Promise<GetDocumentsResponse> {
    console.log('getInsuranceCaseDocuments API call', caseId)
    const response = await prilohyPojistnaUdalost({
      path: { klientId: clientId, udalostId: caseId },
    })
    this.throwIfError(response, 'Error fetching insurance case documents.')
    const data = response.data
    return { success: true, documents: this.mapDocuments(data || []) }
  }

  async getMessageAttachments(clientId: number, messageId: number): Promise<GetDocumentsResponse> {
    console.log('getMessageAttachments API call', messageId)
    const response = await prilohyUkolDetail({
      path: { klientId: clientId, ukolId: messageId },
    })
    this.throwIfError(response, 'Error fetching message attachments.')
    const data = response.data
    return { success: true, documents: this.mapDocuments(data || []) }
  }

  async getGeneralAttachments(): Promise<GetDocumentsResponse> {
    console.log('getGeneralAttachments API call')
    const response = await prilohyObecne()
    this.throwIfError(response, 'Error fetching general attachments.')
    const data = response.data
    return { success: true, documents: this.mapDocuments(data || []) }
  }

  async getClientAttachments(clientId: number): Promise<GetDocumentsResponse> {
    console.log('getClientAttachments API call', clientId)
    const response = await prilohyKlientDetail({
      path: { klientId: clientId },
    })
    this.throwIfError(response, 'Error fetching client attachments.')
    const data = response.data
    return { success: true, documents: this.mapDocuments(data || []) }
  }

  private mapDocuments(documents: (Priloha | PrilohaPu)[]): Document[] {
    return (
      documents.map((document) => {
        const isPrilohaPu = 'podkladID' in document
        return {
          id: document.prilohaID || 0,
          uid: document.prilohaUID || '',
          name: document.prilohaNazev || '',
          extension: document.prilohaExt || '',
          size: document.prilohaVelikost || 0,
          note: document.poznamkaText || '',
          type: document.prilohaTyp || null,
          ...(isPrilohaPu && {
            podkladID: document.podkladID,
            required: document.vyzadovano,
            submitted: document.dolozeno,
          }),
        }
      }) || []
    )
  }

  async getDocumentUrl(documentUid: string): Promise<string> {
    console.log('getDocumentUrl API call', { documentUid })
    const response = await prilohaKey({
      path: { prilohaUID: documentUid },
    })
    if (response instanceof AxiosError || 'error' in response) {
      return ''
    }
    const data = (response as AxiosResponse<string>).data
    const url = _ApiClient.buildUrl<DownloadPrilohaData>({
      path: { guid: data || '' },
      url: '/api/priloha/get/{guid}',
    })
    return _ApiClient.getConfig().baseURL + (url || '').slice(1)
  }

  async getUploadUrl(): Promise<string> {
    console.log('getUploadUrl API call')
    const url = _ApiClient.buildUrl({
      url: '/api/upload',
    })
    return _ApiClient.getConfig().baseURL + (url || '').slice(1)
  }

  async deleteUploadedFile(uid: string): Promise<{ success: boolean; message?: string }> {
    console.log('deleteUploadedFile API call', uid)
    const response = await postApiUploadDeleteByUid({
      path: { uid },
    })
    this.throwIfError(response, 'Error deleting uploaded file.')
    return { success: true }
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ success: boolean; message?: string }> {
    console.log('changePassword API call')
    const response = await postApiAccountChangepassword({
      body: {
        currentPassword,
        newPassword,
      },
    })
    this.throwIfError(response, 'Error changing password.')
    return { success: true }
  }

  async search(
    clientId: number,
    text: string,
    target: SearchTarget | null = null,
  ): Promise<GetSearchResponse> {
    console.log('search API call', { clientId, text, target })
    const typKod = this.mapSearchTarget(target)
    const response = await vyhledat({
      path: { klientId: clientId, text: text + '*' },
      query: typKod ? { typKod } : undefined,
    })
    this.throwIfError(response, 'Error fetching search results.')
    const data = response.data
    const result: FulltextResult = data!
    return {
      success: true,
      contracts:
        result.smlouvyList?.map((smlouva) =>
          smlouva.atributPmvID != null ? this.mapSubcontract(smlouva) : this.mapContract(smlouva),
        ) || [],
      insuranceCases:
        result.pojistneUdalosti?.map((insuranceCase) => this.mapInsuranceCase(insuranceCase)) || [],
    }
  }

  private mapSearchTarget(target: SearchTarget | null): 'S' | 'P' | undefined {
    switch (target) {
      case SearchTarget.contracts:
        return 'S'
      case SearchTarget.insuranceCases:
        return 'P'
      default:
        return undefined
    }
  }

  async getSubcontracts(
    clientId: number,
    contractId: number,
    active: boolean = true,
    expired: boolean = false,
    canceled: boolean = false,
  ): Promise<GetSubcontractsResponse> {
    console.log('getSubcontracts API call', contractId)
    const response = await podsmlouvyList({
      path: { klientId: clientId, smlouvaId: contractId },
      query: { isAktivni: active, isUkoncene: expired, isStornovane: canceled },
    })

    this.throwIfError(response, 'Error fetching subcontracts list.')
    const data = response.data
    return {
      success: true,
      subcontracts: data?.map((subcontract) => this.mapSubcontract(subcontract)) || [],
    }
  }

  async getSubcontractDetail(
    clientId: number,
    subcontractId: number,
  ): Promise<GetContractDetailResponse> {
    console.log('getSubcontractDetail API call', subcontractId)
    const response = await podsmlouvaDetail({
      path: { klientId: clientId, atributPmvID: subcontractId },
    })

    this.throwIfError(response, 'Error fetching subcontract detail.')
    const data = response.data
    return {
      success: true,
      contract: this.mapSubcontractDetail(data!),
    }
  }

  private mapSubcontract(item: PodsmlouvaList | PodsmlouvaDetail | SmlouvaFulltext): Contract {
    const podItem = item as PodsmlouvaList
    return {
      id: item.atributPmvID || 0,
      contractNumber: item.atributPmvCislo || '',
      startDate: this.mapDate(podItem.atributPmvPocatek || ''),
      endDate: this.mapDate(podItem.atributPmvKonec || ''),
      partner: '',
      segment: '',
      subsegment: '',
      product: '',
      attributes: this.mapSubontractAttributes(item),
      validity: ContractValidity.term,
      type: ContractType.subcontract,
      status: this.mapContractStatus(podItem.atributPmvStatus),
      parentContractId: item.smlouvaID && item.smlouvaID > 0 ? item.smlouvaID : undefined,
      parentContractType: toEnumValue(ContractType, item.smlouvaTyp, ContractType.multi),
      cancellationReason: podItem.stornoPopis ?? null,
    }
  }

  private mapSubcontractDetail(item: PodsmlouvaDetail): ContractDetail {
    return {
      ...this.mapSubcontract(item),
      risks: this.mapRisks(item.rizika ?? []),
      policyholder: '',
      renewalDate: null,
      annualPremium: 0,
      premium: 0,
      paymentFrequency: '',
      accountManager: [],
    }
  }

  private mapSubontractAttributes(
    subcontract: PodsmlouvaList | SmlouvaFulltext,
  ): ContractAttribute[] {
    const attributes: ContractAttribute[] = []
    const podItem = subcontract as PodsmlouvaList
    if (subcontract.registracniZnacka) {
      attributes.push({
        name: 'RZ',
        value: subcontract.registracniZnacka || '',
        toolTip: '',
      })
    }
    if (subcontract.vinVozidla) {
      attributes.push({
        name: 'Vin',
        value: subcontract.vinVozidla || '',
        toolTip: '',
      })
    }
    if (podItem.druhVozidlaNazev) {
      attributes.push({
        name: 'Druh',
        value: podItem.druhVozidlaNazev || '',
        toolTip: '',
      })
    }
    if (podItem.tovarniZnackaNazev) {
      attributes.push({
        name: 'Značka',
        value: podItem.tovarniZnackaNazev || '',
        toolTip: '',
      })
    }
    return attributes
  }

  async getInsuranceCases(
    clientId: number,
    contractId?: number,
    type?: ContractType,
    filters?: { open: boolean; closed: boolean; postponed: boolean },
  ): Promise<GetInsuranceCasesResponse> {
    console.log('getInsuranceCases API call', contractId ?? 'all', filters)
    const query = filters
      ? {
          isOtevrena: filters.open,
          isUzavrena: filters.closed,
          isOdlozena: filters.postponed,
        }
      : undefined
    let response
    if (contractId && type != null) {
      response = await pojistneUdalosti({
        path: { klientId: clientId, smlouvaId: contractId, typ: type },
        query,
      })
    } else {
      response = await pojistneUdalostiVse({
        path: { klientId: clientId },
        query,
      })
    }

    this.throwIfError(response, 'Error fetching insurance cases.')
    const data = response.data
    const insuranceCases = (data! || []).map((item) => this.mapInsuranceCase(item))
    return { success: true, insuranceCases }
  }

  private mapInsuranceCase(
    insuranceCase: PojistnaUdalostDetail | PojistnaUdalostDetailData,
  ): InsuranceCase {
    return {
      id: insuranceCase.udalostID || 0,
      uid: insuranceCase.udalostUID || null,
      contractId: insuranceCase.smlouvaID || null,
      contractType: toEnumValue(ContractType, insuranceCase.smlouvaTyp, ContractType.single),
      subcontractId: insuranceCase.atributPmvID || null,
      caseNumber: insuranceCase.udalostCislo || '',
      reportNumber: insuranceCase.hlasenkaCislo || '',
      status: this.mapInsuranceCaseStatus(insuranceCase.udalostStatus),
      date: this.mapDate(insuranceCase.vznikDatum || ''),
      reportDate: this.mapDate(insuranceCase.nahlaseniDatum || ''),
      endDate: this.mapDate(insuranceCase.skodaUkonceni || ''),
      partner: insuranceCase.partnerNazev || null,
      segment: insuranceCase.segmentNazev || null,
      representationType: insuranceCase.zastoupeniTypPopis || null,
      damageType: insuranceCase.skodaTypNazev || null,
      RZ: insuranceCase.registracniZnacka || null,
      Vin: insuranceCase.vinVozidla || null,
      accountManager: this.mapAccountManager([
        {
          spravceNazev: insuranceCase.spravcePU || '',
          mail: insuranceCase.spravceMail || '',
          telefon: insuranceCase.spravceTelefon || '',
        },
      ]),
      location: insuranceCase.skodaMisto || null,
      cause: insuranceCase.skodaPricina || null,
      description: insuranceCase.skodaPopis || null,
      regress: insuranceCase.regres,
    }
  }

  private mapInsuranceCaseStatus(status: number | undefined): InsuranceCaseStatus {
    switch (status) {
      case 30:
        return InsuranceCaseStatus.open
      case 40:
        return InsuranceCaseStatus.closed
      case 50:
        return InsuranceCaseStatus.postponed
      default:
        return InsuranceCaseStatus.open
    }
  }

  async getInsuranceCaseDetail(
    clientId: number,
    caseId: number,
  ): Promise<GetInsuranceCaseDetailResponse> {
    console.log('getInsuranceCaseDetail API call', caseId)
    const response = await pojistnaUdalost({
      path: { klientId: clientId, udalostId: caseId },
    })
    this.throwIfError(response, 'Error fetching insurance case detail.')
    const data = response.data
    return { success: true, insuranceCase: this.mapInsuranceCaseDetail(data!) }
  }

  private mapInsuranceCaseDetail(insuranceCase: PojistnaUdalostDetailData): InsuranceCaseDetail {
    const insurance: InsuranceCase = this.mapInsuranceCase(insuranceCase)
    return {
      ...insurance,
      participants: this.mapParticipants(insuranceCase.ucastniciList || []),
      communication:
        insuranceCase.komunikaceList?.map((message) => this.mapInsuranceCaseComs(message)) || [],
      notes: insuranceCase.poznamky?.map((note: Poznamka) => note.poznamkaText || '') || [],
      settlement: this.mapSettlements(insuranceCase.finance || {}),
      payments:
        insuranceCase.financeZalohyList?.map((zaloha: PojistnaUdalostFinanceZaloha) =>
          this.mapPaymentZaloha(zaloha),
        ) || [],
    }
  }

  private mapSettlements(finance: PojistnaUdalostFinance): Settlement {
    return {
      damageEstimate: finance.skodaOdhad ?? null,
      coParticipation: finance.skodaSpoluucast ?? null,
      totalDamage: finance.skodaTotal ?? null,
      reserve: finance.skodaRezerva ?? null,
      fulfillmentTotal: finance.plneniTotal ?? null,
    }
  }

  private mapInsuranceCaseComs(message: PojistnaUdalostKomunikace): InsuranceCaseComs {
    return {
      id: message.komunikaceID || 0,
      text: message.komunikacePopis || '',
      date: this.mapDate(message.komunikaceDatum || ''),
      note: message.poznamkaText || null,
    }
  }

  private mapParticipants(participants: PojistnaUdalostUcastnik[]): Participant[] {
    return (
      participants.map((participant) => ({
        id: participant.ucastnikID || 0,
        type: participant.ucastnikTypNazev || '',
        name: participant.ucastnikJmeno || '',
        address: participant.ucastnikAdresa || '',
        phone: participant.telefon || '',
        email: participant.mail || '',
        note: participant.poznamkaText || '',
      })) || []
    )
  }

  async getPayments(clientId: number, contractId: number): Promise<GetPaymentsResponse> {
    console.log('getPayments API call', contractId)
    const response = await platbyList({
      path: { klientId: clientId, smlouvaId: contractId },
    })
    this.throwIfError(response, 'Error fetching payments.')
    const data = response.data
    // 204 No Content (no permission / null platby): client normalizes empty body to {} — not an array
    const platby = Array.isArray(data) ? data : null
    return {
      success: true,
      payments: platby?.map((payment) => this.mapPayment(payment)) ?? null,
    }
  }

  private mapPayment(payment: Platba): Payment {
    return {
      paymentDate: this.mapDate(payment.datumUhrady || ''),
      periodFrom: this.mapDate(payment.obdobiOd || ''),
      periodTo: this.mapDate(payment.obdobiDo || ''),
      amount: payment.platbaCastka || 0,
      note: payment.poznamkaText || '',
    }
  }

  private mapPaymentZaloha(zaloha: PojistnaUdalostFinanceZaloha): Payment {
    return {
      paymentDate: this.mapDate(zaloha.zalohaVyplaceno || ''),
      amount: zaloha.zalohaHodnota || 0,
      note: zaloha.poznamkaText || '',
      periodFrom: null,
      periodTo: null,
    }
  }

  async submitMessage(
    clientId: number,
    title: string,
    text: string,
    contractType: ContractType | null,
    contractId: number | null,
    caseId: number | null,
    attachments: Array<{ fileGuid: string; fileName: string }>,
  ): Promise<PostMessageResponse> {
    console.log('submitMessage API call', { clientId, title, contractType, contractId, caseId })

    // Determine binding type and ID
    let ukolVazbaTyp: string = ''
    let ukolVazbaID: number | null = null

    if (caseId) {
      // Insurance case binding
      ukolVazbaTyp = MessageBindingType.insuranceCase
      ukolVazbaID = caseId
    } else if (contractId && contractType !== null) {
      // Contract binding
      ukolVazbaTyp =
        contractType === ContractType.subcontract
          ? MessageBindingType.subcontract
          : MessageBindingType.contract
      ukolVazbaID = contractId
    }
    // If both are null, it's a general message (no binding)

    // Map attachments to SouborInfo format
    const soubory: SouborInfo[] | null =
      attachments.length > 0
        ? attachments.map((att) => ({
            nazevSouboru: att.fileName,
            tempSoubor: att.fileGuid,
          }))
        : null

    const ukolData: UkolInsert = {
      ukolNazev: title,
      ukolPopis: text,
      ukolVazbaTyp: ukolVazbaTyp,
      ukolVazbaID: ukolVazbaID || null,
      soubory: soubory,
    }

    const response = await zpravaInsert({
      path: { klientId: clientId },
      body: ukolData,
    })

    this.throwIfError(response, 'Error submitting message.')
    const data = response.data

    return { success: true, messageId: data?.ukolID }
  }

  async setMessageStatus(
    clientId: number,
    messageId: number,
    status: MessageStatus,
  ): Promise<SetMessageStatusResponse> {
    console.log('setMessageStatus API call', { clientId, messageId, status })
    const statusArg: UkolStatusArg = {
      ukolId: messageId,
      ukolStatus: status,
    }
    const response = await zpravaStatus({
      path: { klientId: clientId },
      body: statusArg,
    })
    this.throwIfError(response, 'Error setting message status.')
    const data = response.data
    return { success: true, changedMessage: this.mapMessage(data!) }
  }

  async getMessages(clientId: number, archived?: boolean): Promise<GetMessagesResponse> {
    console.log('getMessages API call', clientId)
    const response = await zpravyList({
      path: { klientId: clientId },
      query: archived !== undefined ? { archiv: archived } : undefined,
    })
    this.throwIfError(response, 'Error fetching messages.')
    const data = response.data
    return {
      success: true,
      messages: data?.map((ukol) => this.mapMessage(ukol)) || [],
    }
  }

  async getContractMessages(clientId: number, contractId: number): Promise<GetMessagesResponse> {
    console.log('getContractMessages API call', contractId)
    const response = await zpravySmlouvaList({
      path: { klientId: clientId, id: contractId },
    })
    this.throwIfError(response, 'Error fetching contract messages.')
    const data = response.data
    return {
      success: true,
      messages: data?.map((ukol) => this.mapMessage(ukol)) || [],
    }
  }

  async getSubcontractMessages(
    clientId: number,
    subcontractId: number,
  ): Promise<GetMessagesResponse> {
    console.log('getSubcontractMessages API call', subcontractId)
    const response = await zpravyPodsmlouvaList({
      path: { klientId: clientId, id: subcontractId },
    })
    this.throwIfError(response, 'Error fetching subcontract messages.')
    const data = response.data
    return {
      success: true,
      messages: data?.map((ukol) => this.mapMessage(ukol)) || [],
    }
  }

  async getInsuranceCaseMessages(clientId: number, caseId: number): Promise<GetMessagesResponse> {
    console.log('getInsuranceCaseMessages API call', caseId)
    const response = await zpravyPojistnaUdalostList({
      path: { klientId: clientId, id: caseId },
    })
    this.throwIfError(response, 'Error fetching insurance case messages.')
    const data = response.data
    return {
      success: true,
      messages: data?.map((ukol) => this.mapMessage(ukol)) || [],
    }
  }

  private mapMessage(ukol: Ukol): Message {
    // Map ukolStatus to MessageStatus enum (10=created, 20=read, 30=archived)
    let status = MessageStatus.created
    if (ukol.ukolStatus === 20) status = MessageStatus.read
    else if (ukol.ukolStatus === 30) status = MessageStatus.archived

    // Map ukolVazbaTyp to MessageBindingType enum
    const bindingType =
      typeof ukol.ukolVazbaTyp === 'string'
        ? (toEnumValue(MessageBindingType, ukol.ukolVazbaTyp) ?? undefined)
        : undefined

    // Map smlouvaTyp to ContractType enum
    const contractType = toEnumValue(ContractType, ukol.smlouvaTyp, ContractType.single)

    // Map isAdresat to MessageType enum (true = received, false = sent)
    const type: MessageType = ukol.isAdresat === true ? MessageType.received : MessageType.sent

    return {
      id: ukol.ukolID || 0,
      description: ukol.ukolNazev || '',
      status: status,
      startDate: ukol.ukolZahajeni ? new Date(ukol.ukolZahajeni) : new Date(),
      fullDescription: ukol.ukolPopis || undefined,
      category: ukol.ukolKategorieNazev || undefined,
      completionDate: ukol.ukolSplneni ? new Date(ukol.ukolSplneni) : undefined,
      bindingType: bindingType,
      bindingId: ukol.ukolVazbaID || undefined,
      bindingText: ukol.ukolVazba || undefined,
      contractType: contractType,
      type: type,
    }
  }
}
